import React from 'react'
import { connect, useDispatch } from 'react-redux'
import { EntityTypeEnum, WorkflowActions, CrudEventEnum, ProductSectorDetail, SectorStatus, EntityState } from 'api'
import { Locale, Translation } from 'locale'
import { SplitButtonDropdown, ActionItem } from 'view/components/menus'
import { ActionsButtonProps } from 'view/components/datagrid'
import { ModalConsumerProps, withModal } from 'view/shared/modal'
import { CorrelatedResponseCallback } from 'store/notification/notificationTypes'
import { SectorEditDialog, SectorViewDialog } from '../SectorDialog'

export interface SectorActionsButtonProps extends ModalConsumerProps, ActionsButtonProps<ProductSectorDetail> {
  onShowProductEntityDialog: (
    entityTypeEnum: EntityTypeEnum,
    entityId: string,
    correlatedResponseCallback?: CorrelatedResponseCallback
  ) => unknown
}

const mapDispatchToProps = {
  onShowProductEntityDialog: WorkflowActions.createEditWorkflow
}

export const SectorActionsButtonComponent: React.FC<SectorActionsButtonProps> = ({ items, showModal, onShowProductEntityDialog }) => {
  const dispatch = useDispatch()
  const handleEditItemDialog = (entityId: string) => {
    const correlatedResponseCallback = (workflowId: string) => {
      showModal(<SectorEditDialog workflowId={workflowId} entityId={entityId} />)
    }
    onShowProductEntityDialog(EntityTypeEnum.PRODUCT_SECTOR, entityId, correlatedResponseCallback)
  }

  const handleItemStatusChange = (entityId: string, status: SectorStatus) => {
    dispatch(
      WorkflowActions.createEntitiesCreateAndSubmitReferenceData(
        [
          {
            data: { ...items[0].productSector, status },
            entityState: EntityState.EXISTING,
            entityTypeEnum: EntityTypeEnum.PRODUCT_SECTOR,
            id: entityId,
            validationIssues: []
          }
        ],
        []
      )
    )
  }

  const handleShowItemDialog = (entityId: string) => showModal(<SectorViewDialog entityId={entityId} />)

  const canEditDetails = (item: ProductSectorDetail) => {
    const isKnownSector = !item.productSector.system
    return item.availableEvents.includes(CrudEventEnum.UPSERT) && isKnownSector
  }

  const editItems = items.filter(item => canEditDetails(item))
  const deletedItems = editItems.filter(item => item.productSector.status === SectorStatus.DELETED)
  const viewItems = items.filter(item => !canEditDetails(item))

  const actions: ActionItem[] = [
    {
      label: Locale.translate(Translation.TC_ACTION_VIEW_DETAILS),
      onClick: () => handleShowItemDialog(viewItems[0].productSector.id),
      enabled: viewItems.length === 1
    },
    {
      label: Locale.translate(Translation.TC_ACTION_EDIT_DETAILS),
      onClick: () => handleEditItemDialog(editItems[0].productSector.id),
      enabled: editItems.length === 1
    },
    {
      label: Locale.translate(Translation.TC_ACTION_DELETE),
      onClick: () => handleItemStatusChange(editItems[0].productSector.id, SectorStatus.DELETED),
      enabled: editItems.length === 1 && deletedItems.length === 0
    },
    {
      label: Locale.translate(Translation.TC_ACTION_ENABLE),
      onClick: () => handleItemStatusChange(editItems[0].productSector.id, SectorStatus.ENABLED),
      enabled: deletedItems.length === 1
    }
  ]

  return <SplitButtonDropdown dataTestId={'SectorActionsButton'} actions={actions} maxButtons={1} autoHideEllipsis={true} />
}

export const SectorActionsButton = withModal(connect(null, mapDispatchToProps)(SectorActionsButtonComponent))
